import type { UnitEndings, UnitExitReason, UnitState, UnitUsage, WorkerUnit } from '../../../services/adminUnits';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const ExitReasonLabels: Record<UnitExitReason, string> = {
  IdleTimeout: 'idle timeout',
  MaxJobs: 'most jobs',
  MaxAge: 'longest life',
  Drain: 'drained',
  LowDisk: 'low disk',
  HostStopped: 'stopped by the host',
  Lost: 'lost',
};

export const exitReasonLabel = (reason: UnitExitReason) => ExitReasonLabels[reason];

const SummaryStates: UnitState[] = ['Starting', 'Idle', 'Busy', 'Draining'];

export const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** A span in its two largest parts, such as "3 h 5 min"; under a minute, in seconds. */
export function formatSpan(milliseconds: number): string {
  const span = Math.max(0, milliseconds);
  if (span < MINUTE_MS) return `${Math.floor(span / SECOND_MS)} s`;
  if (span < HOUR_MS) return `${Math.floor(span / MINUTE_MS)} min`;
  if (span < DAY_MS) {
    const minutes = Math.floor((span % HOUR_MS) / MINUTE_MS);
    return `${Math.floor(span / HOUR_MS)} h` + (minutes ? ` ${minutes} min` : '');
  }
  const hours = Math.floor((span % DAY_MS) / HOUR_MS);
  return `${Math.floor(span / DAY_MS)} d` + (hours ? ` ${hours} h` : '');
}

/** The span from `from` to `asOf`, both instants as the API writes them. */
export const spanUntil = (from: string, asOf: string) => formatSpan(Date.parse(asOf) - Date.parse(from));

/** Bytes as GB of 10^9 bytes, to one decimal. */
export const formatGb = (bytes: number | null) => (bytes == null ? '–' : `${(bytes / 1e9).toFixed(1)} GB`);

export const formatCores = (cores: number | null) => (cores == null ? '–' : `${cores.toFixed(2)} cores`);

export const formatCpuAveragePeak = (usage: UnitUsage | null) =>
  usage == null ? '–' : `${usage.cpuAverageCores.toFixed(2)} / ${usage.cpuPeakCores.toFixed(2)} cores`;

export const formatMemoryAveragePeak = (usage: UnitUsage | null) =>
  usage == null ? '–' : `${(usage.memoryAverageBytes / 1e9).toFixed(1)} / ${(usage.memoryPeakBytes / 1e9).toFixed(1)} GB`;

export const formatMoment = (value: string) =>
  new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** The time of day alone, such as "14:05", in the viewer's time zone. */
export const formatClock = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

export function unitsSummary(units: WorkerUnit[]): string {
  const states = SummaryStates.map(
    (state) => `${units.filter((unit) => unit.state === state).length} ${state.toLowerCase()}`
  );
  return `${plural(units.length, 'unit', 'units')}: ${states.join(' · ')}`;
}

export function endingsSummary(endings: UnitEndings[]): string {
  const total = endings.reduce((sum, ending) => sum + ending.count, 0);
  if (total === 0) return 'No unit ended in the last 24 hours.';
  const reasons = endings.map((ending) => `${ExitReasonLabels[ending.reason]} ${ending.count}`);
  return `${plural(total, 'unit', 'units')} ended in the last 24 hours: ${reasons.join(', ')}.`;
}
